import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { tenantAdminService } from "@/services/tenant-admin-service";
import { adminUsersService } from "@/services/admin-users-service";
import { fileService } from "@/services/ServiceRegistry";
import {
  CreateFrontDeskUserDto,
  CreateHotelAdminUserDto,
  CreateTenantUserDto,
  UpdateUserDto,
  UserFilters,
} from "@/types/user";
import type { UsersKpiFilters } from "@/types/users-kpis";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { isPlatformAdminRole } from "@/utils/accessControl";
import { UserFormData } from "../utils/userSchemas";
import { buildCreateTenantUserPayload } from "../utils/createUserPayload";

function getAdminUsersPagination(filters: UserFilters): {
  limit?: number;
  offset?: number;
} {
  const limit = filters.limit ?? filters.perPage;
  const offset =
    filters.offset ??
    (typeof filters.currentPage === "number" &&
    typeof filters.perPage === "number"
      ? (filters.currentPage - 1) * filters.perPage
      : undefined);

  return {
    ...(typeof limit === "number" ? { limit } : {}),
    ...(typeof offset === "number" ? { offset } : {}),
  };
}

function buildAdminUsersFilters(
  filters?: UserFilters,
): UserFilters | undefined {
  if (!filters) {
    return undefined;
  }

  const nonEmpty = <T>(values?: T[]): T[] | undefined =>
    values?.length ? values : undefined;

  return {
    tenantId: filters.tenantId,
    tenantName: filters.tenantName,
    tenantSlug: filters.tenantSlug,
    roles: nonEmpty(filters.roles),
    positions: nonEmpty(filters.positions),
    customRoleIds: nonEmpty(filters.customRoleIds),
    accessGroupIds: nonEmpty(filters.accessGroupIds),
    hotelId: filters.hotelId,
    search: filters.search,
    statusActivity: filters.statusActivity,
    createdFrom: filters.createdFrom,
    createdTo: filters.createdTo,
    sortBy: filters.sortBy ?? filters.sortField,
    sortDirection: filters.sortDirection,
    ...getAdminUsersPagination(filters),
  };
}

export const userKeys = {
  all: ["users"] as const,
  lists: () => [...userKeys.all, "list"] as const,
  list: (filters?: UserFilters) => [...userKeys.lists(), filters] as const,
  details: () => [...userKeys.all, "detail"] as const,
  detail: (id: string) => [...userKeys.details(), id] as const,
  counts: () => [...userKeys.all, "count"] as const,
  count: (filters?: UserFilters) => [...userKeys.counts(), filters] as const,
  kpis: (filters?: UsersKpiFilters) =>
    [...userKeys.all, "kpis", filters] as const,
  filterOptions: (filters?: UserFilters) =>
    [...userKeys.all, "filter-options", filters] as const,
};

export const useUsers = (filters?: UserFilters, enabled = true) => {
  const { userProfile } = useAuthenticatedUser();
  const role = userProfile?.role ?? null;
  const platformAdminSession = isPlatformAdminRole(userProfile);

  return useQuery({
    queryKey: userKeys.list(filters),
    queryFn: async () => {
      if (platformAdminSession && !filters?.tenantId) {
        const response = await adminUsersService.getUsers(
          buildAdminUsersFilters(filters),
        );
        return response.items;
      }

      return tenantAdminService.getUsers(filters);
    },
    staleTime: 5 * 60 * 1000,
    enabled: enabled && Boolean(role),
  });
};

export const useUsersCount = (filters?: UserFilters, enabled = true) => {
  const { userProfile } = useAuthenticatedUser();
  const role = userProfile?.role ?? null;
  const platformAdminSession = isPlatformAdminRole(userProfile);

  return useQuery({
    queryKey: userKeys.count(filters),
    queryFn: async () => {
      if (platformAdminSession && !filters?.tenantId) {
        const response = await adminUsersService.getUsers(
          buildAdminUsersFilters(filters),
        );
        return response.total;
      }

      return tenantAdminService.getUsersCount(filters);
    },
    staleTime: 5 * 60 * 1000,
    enabled: enabled && Boolean(role),
  });
};

export const useUsersKpis = (filters?: UsersKpiFilters, enabled = true) => {
  const { userProfile } = useAuthenticatedUser();
  const role = userProfile?.role ?? null;

  return useQuery({
    queryKey: userKeys.kpis(filters),
    queryFn: () => tenantAdminService.getUsersKpis(filters),
    staleTime: 60 * 1000,
    enabled: enabled && Boolean(role),
  });
};

export const useUserFilterOptions = (filters?: UserFilters, enabled = true) => {
  const { userProfile } = useAuthenticatedUser();
  const platformAdminSession = isPlatformAdminRole(userProfile);

  return useQuery({
    queryKey: userKeys.filterOptions(filters),
    queryFn: () =>
      platformAdminSession
        ? adminUsersService.getUserFilterOptions(
            buildAdminUsersFilters(filters),
          )
        : tenantAdminService.getUserFilterOptions(filters),
    staleTime: 5 * 60 * 1000,
    enabled,
  });
};

export const useUser = (id: string) => {
  const { userProfile } = useAuthenticatedUser();
  const role = userProfile?.role ?? null;
  const platformAdminSession = isPlatformAdminRole(userProfile);

  return useQuery({
    queryKey: userKeys.detail(id),
    queryFn: () => tenantAdminService.getUserById(id),
    staleTime: 5 * 60 * 1000,
    enabled: !!id && Boolean(role) && !platformAdminSession,
  });
};

export const useCreateHotelAdminUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userData: CreateHotelAdminUserDto) =>
      tenantAdminService.createHotelAdminUser(userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userKeys.counts() });
    },
  });
};

export const useCreateFrontDeskUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userData: CreateFrontDeskUserDto) =>
      tenantAdminService.createFrontDeskUser(userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userKeys.counts() });
    },
  });
};

export const useCreateTenantUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userData: CreateTenantUserDto) =>
      tenantAdminService.createUser(userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userKeys.counts() });
    },
  });
};

export const useCreateTenantScopedUser = () => {
  const queryClient = useQueryClient();
  const { userProfile } = useAuthenticatedUser();
  const platformAdminSession = isPlatformAdminRole(userProfile);

  return useMutation({
    mutationFn: ({
      tenantId,
      userData,
    }: {
      tenantId?: string;
      userData: CreateTenantUserDto;
    }) => {
      if (platformAdminSession && tenantId) {
        return adminUsersService.createTenantUser(tenantId, userData);
      }

      return tenantAdminService.createUser(userData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userKeys.counts() });
      queryClient.invalidateQueries({ queryKey: ["staff"] });
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => tenantAdminService.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userKeys.counts() });
    },
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { id: string; data: UpdateUserDto }>({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserDto }) =>
      tenantAdminService.updateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.details() });
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userKeys.counts() });
      queryClient.invalidateQueries({ queryKey: ["staff"] });
    },
  });
};

export const useUserCreate = () => {
  const createTenantUserMutation = useCreateTenantUser();

  const handleUserCreate = async (userData: UserFormData, photoFile?: File) => {
    try {
      let photoFileId: string | undefined;

      if (userData?.hotelId === "") {
        delete userData.hotelId;
      }

      if (photoFile) {
        try {
          photoFileId = await fileService.uploadFileWithPresignedUrl(photoFile);
        } catch (photoError) {
          console.error("Failed to upload photo:", photoError);
        }
      }

      const response = await createTenantUserMutation.mutateAsync(
        buildCreateTenantUserPayload(userData, photoFileId),
      );

      return response.id;
    } catch (error) {
      console.error("Failed to create user:", error);
      throw error;
    }
  };

  return {
    handleUserSubmit: handleUserCreate,
    isLoading: createTenantUserMutation.isPending,
    error: createTenantUserMutation.error,
  };
};

export const useUserUpdate = (userId: string) => {
  const updateUserMutation = useUpdateUser();

  const handleUserUpdate = async (
    userData: UserFormData,
    photoFile?: File,
    currentPhotoId?: string,
    canUpdateRole = true,
  ) => {
    try {
      let photoFileId: string | undefined = currentPhotoId;

      if (photoFile) {
        try {
          photoFileId = await fileService.uploadFileWithPresignedUrl(photoFile);
        } catch (photoError) {
          console.error("Failed to upload photo:", photoError);
        }
      }

      const updateData: UpdateUserDto = {
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        phone: userData.phone,
        ...(canUpdateRole ? { role: userData.role } : {}),
        preferredLanguage: userData.preferredLanguage,
        photoId: photoFileId,
        ...(userData?.hotelId ? { hotelId: userData.hotelId } : {}),
      };

      await updateUserMutation.mutateAsync({
        id: userId,
        data: updateData,
      });
      if (canUpdateRole) {
        await tenantAdminService.replaceUserCustomRoles(
          userId,
          userData.customRoleId ? [userData.customRoleId] : [],
        );
      }
    } catch (error) {
      console.error("Failed to update user:", error);
      throw error;
    }
  };

  return {
    handleUserSubmit: handleUserUpdate,
    isLoading: updateUserMutation.isPending,
    error: updateUserMutation.error,
  };
};
